/**
 * Robust dream sentencization for granular semantic matching
 * Handles punctuation boundaries and conjunctive clauses for better specificity
 */

export interface DreamSegment {
  text: string;
  index: number;
  type: 'sentence' | 'clause';
  originalPosition: { start: number; end: number };
}

export class DreamSentencizer {
  // Reliable conjunctive markers that clearly signal new semantic units in narratives
  private static readonly CONJUNCTIVE_MARKERS = [
    // Most reliable clause connectors (almost never ambiguous)
    'and',
    'or',
    'but',
    // Strong narrative transitions
    'then',
    'meanwhile',
    // Strong contrast markers
    'however',
    'although',
    // Strong causal markers
    'therefore',
    'consequently',
    'thus',
    // Note: Excluded ambiguous words like 'later' (could be "see you later"),
    // 'after' (could be "after dinner"), 'so' (could be "so happy"), etc.
  ];

  // Sentence boundary punctuation
  private static readonly SENTENCE_ENDINGS = ['.', '!', '?', '...', ';', ':', '-', ','];

  /**
   * Split dream text into meaningful semantic segments
   */
  static sentencize(
    dreamText: string,
    options: {
      minSegmentLength?: number;
      maxSegmentLength?: number;
      preserveContext?: boolean;
    } = {}
  ): DreamSegment[] {
    const { minSegmentLength = 10 } = options;

    console.log(
      `📝 Sentencizing dream: "${dreamText.slice(0, 60)}..." (${dreamText.length} chars)`
    );

    let segments: DreamSegment[] = [];
    const currentText = dreamText.trim();

    if (currentText.length === 0) return segments;

    // Step 1: Primary split on sentence boundaries
    const sentenceParts = this.splitOnPunctuation(currentText);

    // Step 2: Split all sentences on conjunctive clauses (not just long ones)
    for (const sentencePart of sentenceParts) {
      // Always try conjunction splitting for better granularity
      const clauses = this.splitOnConjunctions(sentencePart);

      if (clauses.length > 1) {
        // Successfully split into clauses
        for (const clause of clauses) {
          if (clause.text.length >= minSegmentLength) {
            segments.push({
              text: clause.text,
              index: segments.length,
              type: 'clause',
              originalPosition: clause.position,
            });
          }
        }
      } else {
        // No valid splits found, keep as single segment
        if (sentencePart.text.length >= minSegmentLength) {
          segments.push({
            text: sentencePart.text,
            index: segments.length,
            type: 'sentence',
            originalPosition: sentencePart.position,
          });
        }
      }
    }

    // Step 3: Post-processing for very short segments
    segments = this.mergeShortSegments(segments, minSegmentLength);

    console.log(`   📝 Split into ${segments.length} segments:`);
    segments.forEach((seg, i) => {
      console.log(
        `      ${i + 1}. [${seg.type}] "${seg.text.slice(0, 50)}${seg.text.length > 50 ? '...' : ''}"`
      );
    });

    return segments;
  }

  /**
   * Split text on sentence punctuation
   */
  private static splitOnPunctuation(
    text: string
  ): Array<{ text: string; position: { start: number; end: number } }> {
    const parts: Array<{ text: string; position: { start: number; end: number } }> = [];
    // const currentStart = 0; // Reserved for future use

    // Find sentence boundaries
    const sentences = text.split(/([.!?,:;-]+)/);
    let position = 0;

    for (let i = 0; i < sentences.length; i += 2) {
      const sentence = sentences[i];
      const punctuation = sentences[i + 1] || '';

      if (sentence.trim()) {
        const fullSentence = (sentence + punctuation).trim();
        const start = position;
        const end = start + fullSentence.length;

        parts.push({
          text: fullSentence,
          position: { start, end },
        });
      }

      position += sentence.length + punctuation.length;
    }

    return parts;
  }

  /**
   * Split long sentences on conjunctive markers (with context validation)
   */
  private static splitOnConjunctions(sentencePart: {
    text: string;
    position: { start: number; end: number };
  }): Array<{ text: string; position: { start: number; end: number } }> {
    const { text, position } = sentencePart;
    const parts: Array<{ text: string; position: { start: number; end: number } }> = [];

    // Build regex for conjunctive markers (word boundaries)
    const conjunctionPattern = new RegExp(
      '\\b(' + this.CONJUNCTIVE_MARKERS.join('|') + ')\\b',
      'gi'
    );

    let lastEnd = 0;
    let match;

    while ((match = conjunctionPattern.exec(text)) !== null) {
      const conjunctionWord = match[0].toLowerCase();
      const beforeConjunction = text.slice(lastEnd, match.index).trim();
      const afterConjunction = text
        .slice(match.index + match[0].length, match.index + match[0].length + 20)
        .trim();

      // Validate this is a real clause boundary, not a phrase component
      const isValidSplit = this.isValidConjunctiveSplit(
        conjunctionWord,
        beforeConjunction,
        afterConjunction,
        match.index
      );

      if (isValidSplit && match.index > 20 && beforeConjunction.length > 15) {
        parts.push({
          text: beforeConjunction,
          position: {
            start: position.start + lastEnd,
            end: position.start + match.index,
          },
        });
        lastEnd = match.index;
      }
    }

    // Add the remaining part
    const remaining = text.slice(lastEnd).trim();
    if (remaining.length > 15) {
      parts.push({
        text: remaining,
        position: {
          start: position.start + lastEnd,
          end: position.end,
        },
      });
    }

    // If no good splits found, return original
    return parts.length > 0 ? parts : [sentencePart];
  }

  /**
   * Validate if a conjunctive marker represents a true clause boundary
   */
  private static isValidConjunctiveSplit(
    conjunction: string,
    before: string,
    after: string,
    position: number
  ): boolean {
    // Don't split at the very beginning or end
    if (position < 10 || after.length < 8) return false;

    // Ensure both before and after contain substantial content
    if (before.length < 10 || after.length < 8) return false;

    // Special handling for highly reliable conjunctions
    if (
      conjunction.toLowerCase() === 'and' ||
      conjunction.toLowerCase() === 'but' ||
      conjunction.toLowerCase() === 'or'
    ) {
      // These are almost always valid clause boundaries, use lighter validation
      return true;
    }

    // For other conjunctions, check for complete thoughts
    const beforeHasVerb =
      /\b(was|were|is|are|had|have|went|came|saw|did|said|felt|became|turned)\b/i.test(before);
    const afterHasContent = /\b(I|he|she|it|they|we|you|the|a|an)\b/i.test(after);

    // Both parts should feel like complete thoughts
    return beforeHasVerb && afterHasContent;
  }

  /**
   * Merge adjacent short segments to avoid over-fragmentation
   */
  private static mergeShortSegments(segments: DreamSegment[], minLength: number): DreamSegment[] {
    const merged: DreamSegment[] = [];
    let currentMerged: DreamSegment | null = null;

    for (const segment of segments) {
      if (segment.text.length < minLength && currentMerged) {
        // Merge with previous segment
        currentMerged.text = currentMerged.text + ' ' + segment.text;
        currentMerged.originalPosition.end = segment.originalPosition.end;
        currentMerged.type = 'clause'; // Merged segments become clauses
      } else if (segment.text.length < minLength && merged.length > 0) {
        // Merge with previous completed segment
        const lastSegment = merged[merged.length - 1];
        lastSegment.text = lastSegment.text + ' ' + segment.text;
        lastSegment.originalPosition.end = segment.originalPosition.end;
        lastSegment.type = 'clause';
      } else {
        // Complete any pending merge
        if (currentMerged) {
          merged.push(currentMerged);
          currentMerged = null;
        }

        // Add new segment
        if (segment.text.length >= minLength) {
          merged.push(segment);
        } else {
          currentMerged = { ...segment };
        }
      }
    }

    // Complete final merge
    if (currentMerged) {
      merged.push(currentMerged);
    }

    return merged;
  }

  /**
   * Get contextual information for a segment
   */
  static getSegmentContext(
    segment: DreamSegment,
    allSegments: DreamSegment[]
  ): {
    previousSegment?: DreamSegment;
    nextSegment?: DreamSegment;
    contextualText: string;
  } {
    const currentIndex = allSegments.findIndex(s => s.index === segment.index);
    const previousSegment = currentIndex > 0 ? allSegments[currentIndex - 1] : undefined;
    const nextSegment =
      currentIndex < allSegments.length - 1 ? allSegments[currentIndex + 1] : undefined;

    // Create contextual text (current + some surrounding context)
    let contextualText = segment.text;

    if (previousSegment && previousSegment.text.length < 100) {
      contextualText = previousSegment.text + ' ' + contextualText;
    }

    if (nextSegment && nextSegment.text.length < 100) {
      contextualText = contextualText + ' ' + nextSegment.text;
    }

    return {
      previousSegment,
      nextSegment,
      contextualText,
    };
  }
}
